import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { ID, withTransaction } from '@datorama/akita';
import { Position } from './position.model';
import { PositionsStore } from './positions.store';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { finalize, Observable, Subject } from 'rxjs';
import { Acct } from '../../../shared/state/accts/acct.model';
import { InstrumentType } from '../../../shared/state/instruments/instrument.model';
import { Currency } from '../../../shared/components/currency-selector/currency-country-codes';
import { PositionPerformanceDTO } from './performance.dto';
import { addHours, startOfYear } from 'date-fns';
import { Measurements } from './measurements.dto';
import { Cacheable, CacheBuster } from 'ts-cacheable';

const cacheBuster$ = new Subject<void>();

@Injectable({ providedIn: 'root' })
export class PositionsService implements OnDestroy {
    unsubscribe$ = new Subject();
    constructor(
        private positionsStore: PositionsStore,
        private appConfig: AppConfigQuery,
        private http: HttpClient
    ) {}

    @Cacheable({
        async: true,
        cacheBusterObserver: cacheBuster$,
        maxAge: 1000 * 60 * 1 // 1 minute
    })
    get(
        options:
            | Partial<{
                  accountIds: Array<Acct['accountId']>;
                  includeNetted: boolean;
                  date: Date;
                  persistToStore: boolean;
                  instrumentType: Array<InstrumentType>;
                  baseCurrency: Currency;
              }>
            | Array<Acct['accountId']>,
        includeNettedOption?: boolean
    ) {
        let accountIds: Array<Acct['accountId']>;
        let includeNetted = false;
        let persistToStore = false; // false by default

        if (Array.isArray(options)) {
            accountIds = options;
            includeNetted = includeNettedOption ?? false;
        } else if (typeof options === 'object') {
            accountIds = options.accountIds;
            includeNetted = options.includeNetted ?? false;
            persistToStore = options.persistToStore ?? false;
        }
        const accounts = accountIds?.join(',');
        let params: Partial<{
            accountIds: Array<Acct['accountId']>;
            includeNetted: boolean;
            lastValueDateInclusive: string;
            persistToStore: boolean;
            instrumentType: string;
            baseCurrency: Currency;
        }> = {
            includeNetted
        };
        if (!Array.isArray(options)) {
            if (options.date) {
                params = {
                    ...params,
                    lastValueDateInclusive: options.date.toISOString(),
                    instrumentType: 'CASH'
                };
            }
            if (options.instrumentType) {
                params.instrumentType = options.instrumentType.join(',');
            }
            if (options.baseCurrency) {
                params.baseCurrency = options.baseCurrency;
            }
        }

        const accountId = accounts ? `?accountId=${accounts}` : '';
        if (persistToStore) {
            this.positionsStore.setLoading(true);
        }
        return this.http
            .get<Position[]>(
                `${this.appConfig.get().gatewayBaseUrl}/view/positions${accountId}`,
                {
                    params
                }
            )
            .pipe(
                withTransaction((entities) => {
                    if (persistToStore) {
                        this.positionsStore.update((state) => ({
                            ...state,
                            ui: {
                                ...state.ui,
                                includeNetted,
                                updatedAt: new Date()
                            }
                        }));
                        this.positionsStore.set(entities);
                        this.positionsStore.setLoading(false);
                    }
                })
            );
    }

    @CacheBuster({
        cacheBusterNotifier: cacheBuster$,
        isInstant: true
    })
    bustCache() {}

    /** get positions for use in specific context without persisting data to store */
    @Cacheable({
        maxCacheCount: 3
    })
    getWithCache(
        options:
            | Partial<{
                  accountIds: Array<Acct['accountId']>;
                  includeNetted: boolean;
                  date: Date;
                  persistToStore: boolean;
                  instrumentType: Array<InstrumentType>;
                  baseCurrency: Currency;
              }>
            | Array<Acct['accountId']>,
        includeNettedOption?: boolean
    ) {
        let accountIds: Array<Acct['accountId']>;
        let includeNetted = false;
        if (Array.isArray(options)) {
            accountIds = options;
            includeNetted = includeNettedOption ?? false;
        } else if (typeof options === 'object') {
            accountIds = options.accountIds;
            includeNetted = options.includeNetted ?? false;
        }
        const accounts = accountIds?.join(',');
        let params: Partial<{
            accountIds: Array<Acct['accountId']>;
            includeNetted: boolean;
            lastValueDateInclusive: string;
            instrumentType: string;
            baseCurrency: Currency;
        }> = {
            includeNetted
        };
        if (!Array.isArray(options)) {
            if (options.date) {
                params = {
                    ...params,
                    lastValueDateInclusive: options.date.toISOString(),
                    instrumentType: 'CASH'
                };
            }
            if (options.instrumentType) {
                params.instrumentType = options.instrumentType.join(',');
            }
            if (options.baseCurrency) {
                params.baseCurrency = options.baseCurrency;
            }
        }
        const accountId = accounts ? `?accountId=${accounts}` : '';
        return this.http.get<Position[]>(
            `${this.appConfig.get().gatewayBaseUrl}/view/positions${accountId}`,
            {
                params
            }
        );
    }

    /** waitForPositionUpdate - long poll to wait for position to be updated by transaction
     * @param instrumentId - instrumentId of transaction
     * @param accountId - accountId used in transaction
     * @param updatingTransactionId - transactionId affection position
     */
    waitForPositionUpdate(
        instrumentId?: number,
        accountId?: number,
        updatingTransactionId?: number
    ) {
        return this.http.get(
            `${this.appConfig.get().gatewayBaseUrl}/view/positions/update`,
            {
                params: {
                    accountId: accountId,
                    instrumentId: instrumentId,
                    updatingTransactionId: updatingTransactionId
                }
            }
        );
    }
    @Cacheable({
        async: true,
        maxCacheCount: 1 // 20 seconds
    })
    getPerformance(
        instrumentType: Array<InstrumentType>,
        fromDate: Date,
        toDate: Date,
        baseCurrency?: Currency,
        accountIds?: Array<Acct['accountId']>
    ) {
        let effectiveFromDate = fromDate;
        let effectiveToDate = toDate;
        if (!fromDate) {
            effectiveFromDate = startOfYear(new Date());
        }
        if (!toDate) {
            effectiveToDate = new Date();
        }
        const tzOffset = new Date().getTimezoneOffset() / 60;
        effectiveFromDate = addHours(effectiveFromDate, -tzOffset);
        effectiveToDate = addHours(effectiveToDate, -tzOffset);
        this.positionsStore.setLoading(true);
        return this.http
            .get<PositionPerformanceDTO[]>(
                `${this.appConfig.get().gatewayBaseUrl}/view/positions/performance/`,
                {
                    params: {
                        instrumentType: instrumentType.join(','),
                        fromDate: effectiveFromDate.toISOString(),
                        toDate: effectiveToDate.toISOString(),
                        baseCurrency: baseCurrency ?? 'SEK',
                        accountId: accountIds?.join(',')
                        // aggregateByInstrument: false
                    }
                }
            )
            .pipe(
                finalize(() => {
                    this.positionsStore.setLoading(false);
                })
            );
    }

    @Cacheable({
        async: true,
        maxCacheCount: 1 // 5 seconds
    })
    getPerformanceMeasurements(
        measurementTypes: Array<string>,
        fromDate: Date,
        toDate: Date,
        baseCurrency?: Currency,
        accountIds?: Array<Acct['accountId']>
    ): Observable<Measurements> {
        this.positionsStore.setLoading(true);
        const tzOffset = new Date().getTimezoneOffset() / 60;
        fromDate = addHours(fromDate, -tzOffset);
        toDate = addHours(toDate, -tzOffset);
        return this.http
            .get<Measurements>(
                `${this.appConfig.get().gatewayBaseUrl}/view/performance/measurements`,
                {
                    params: {
                        measurementTypes: measurementTypes.join(','),
                        fromDate: fromDate.toISOString(),
                        toDate: toDate.toISOString(),
                        baseCurrency: baseCurrency ?? 'SEK',
                        accountId: accountIds?.join(',')
                    }
                }
            )
            .pipe(
                finalize(() => {
                    this.positionsStore.setLoading(false);
                })
            );
    }

    includeNetted(includeNetted: boolean) {
        this.positionsStore.update((state) => ({
            ...state,
            ui: {
                ...state.ui,
                includeNetted
            }
        }));
    }

    update(id, position: Partial<Position>) {
        this.positionsStore.update(id, position);
    }

    remove(id: ID) {
        this.positionsStore.remove(id);
    }

    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
